import type { CourtCard } from "../api.js";

/**
 * Multi-court entry — FR-ENTRY-13, FR-ENTRY-16, AC-34, ENTRY-19.
 *
 * With two or more courts the app observes rather than directs. FR-ENTRY-13
 * says why plainly: players self-organise, swap courts freely, and the
 * observed pattern — the first court to finish takes the bench player while
 * those coming off join the later-finishing court — is too fluid to model
 * reliably. So there is no queue, no proposal, and deliberately no "on next".
 *
 * What each card carries instead is the last four who played there, because
 * 88% of consecutive games share players and pre-filling from them is what
 * keeps the cost at three taps: court, winning side, score.
 *
 * A court nobody has played on still gets a card. It is exactly where the next
 * game goes, and leaving it off the screen is the dead end ENTRY-19 names.
 */
export function CourtCards({
  courts,
  nameOf,
  onPick,
}: {
  courts: CourtCard[];
  nameOf: (id: string) => string;
  onPick: (court: CourtCard) => void;
}): React.JSX.Element {
  return (
    <section className="courts">
      <h2>Courts</h2>
      {courts.map((court) => (
        <button key={court.courtNumber} className="court-card" onClick={() => onPick(court)}>
          <div className="row space-between">
            <strong>Court {court.courtNumber}</strong>
            <span className="muted small">
              {court.gamesOnThisCourt} {court.gamesOnThisCourt === 1 ? "game" : "games"}
            </span>
          </div>

          {court.lastSideA.length > 0 ? (
            <span className="court-last">
              {court.lastSideA.map(nameOf).join(" & ")}
              {court.lastScore ? ` ${court.lastScore.a}–${court.lastScore.b} ` : " v "}
              {court.lastSideB.map(nameOf).join(" & ")}
            </span>
          ) : (
            <span className="muted small">Nothing logged here yet — tap to pick four</span>
          )}
        </button>
      ))}
      {/* The bench strip shows counts only on multi-court; FR-ENTRY-13 is
          explicit that there is no proposal to make. */}
      <p className="muted small">
        Tap a court to log its next result. The queue below is a record of who
        has played, not an order — with more than one court, people sort
        themselves out.
      </p>
    </section>
  );
}
